import { Alert, Platform } from "react-native";

/**
 * The one place the app asks a yes/no question or states a notice.
 *
 * `react-native-web` ships `Alert` as a no-op class, so an action passed as an Alert button's
 * `onPress` silently never runs on the web builds. Native raises the OS alert, which is the right
 * convention on a phone and honours both the labels and the destructive style. Web queues the
 * prompt for `DialogHost`, which renders it as an in-app dialog with the same labels and styling.
 */

type Prompt = {
  title: string;
  message: string;
};

export type ConfirmOptions = Prompt & {
  confirmLabel: string;
  /** Defaults to "Cancel". */
  cancelLabel?: string;
  destructive?: boolean;
};

/** A prompt waiting on the web host. A notice carries no `confirm`, only an acknowledgement. */
export type WebDialog = Prompt & {
  id: number;
  confirm?: { label: string; cancelLabel: string; destructive: boolean; };
  settle: (confirmed: boolean) => void;
};

// A queue, not a single slot. A prompt raised while another is open must not replace the first, or
// the first resolver is dropped, its `await` never settles, and the operator's action does nothing.
let queue: WebDialog[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function enqueue(dialog: Omit<WebDialog, "id">): number {
  const id = nextId++;
  queue = [...queue, { ...dialog, id }];
  listeners.forEach((listener) => listener());
  return id;
}

/** Subscribe the host to the queue. */
export function subscribeToDialogs(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The prompt the host should be showing, or `null` while there is nothing to ask. */
export function currentDialog(): WebDialog | null {
  return queue[0] ?? null;
}

/** Settle one prompt and let the next through. An id already answered is ignored. */
export function answerDialog(id: number, confirmed: boolean): void {
  const dialog = queue.find((entry) => entry.id === id);
  if (!dialog) return;
  queue = queue.filter((entry) => entry.id !== id);
  listeners.forEach((listener) => listener());
  dialog.settle(confirmed);
}

/** Ask the operator to confirm, and resolve to what they chose. */
export function askConfirm(opts: ConfirmOptions): Promise<boolean> {
  return askWithdrawableConfirm(opts).answered;
}

/**
 * Ask the operator to confirm, and return a `withdraw` handle so that on web a prompt does not
 * outlive the screen that raised it. The panel belongs to the root host, so an unmounted screen
 * would otherwise leave it over the next screen with a stale action behind it. `withdraw` settles
 * the promise `false` through the same queue path as an answer, and is safe to call twice or after
 * an answer.
 *
 * On native `withdraw` does nothing, and the orphaned prompt can still appear. React Native cannot
 * dismiss a presented `Alert`, since the platform owns it, and `Alert.alert` does not block the JS
 * thread, so a mutation callback can remove the screen behind it. Closing that gap would mean
 * giving up the OS alert.
 */
export function askWithdrawableConfirm(
  opts: ConfirmOptions,
): { answered: Promise<boolean>; withdraw: () => void; } {
  if (Platform.OS === "web") {
    let settle!: (confirmed: boolean) => void;
    const answered = new Promise<boolean>((resolve) => {
      settle = resolve;
    });
    const id = enqueue({
      title: opts.title,
      message: opts.message,
      confirm: {
        label: opts.confirmLabel,
        cancelLabel: opts.cancelLabel ?? "Cancel",
        destructive: !!opts.destructive,
      },
      settle,
    });
    return { answered, withdraw: () => answerDialog(id, false) };
  }
  // No `options` argument: Android reads `cancelable` from it, and without it the dialog cannot be
  // dismissed, so the promise always settles through a button.
  const answered = new Promise<boolean>((resolve) => {
    Alert.alert(opts.title, opts.message, [
      { text: opts.cancelLabel ?? "Cancel", style: "cancel", onPress: () => resolve(false) },
      {
        text: opts.confirmLabel,
        style: opts.destructive ? "destructive" : "default",
        onPress: () => resolve(true),
      },
    ]);
  });
  return { answered, withdraw: () => {} };
}

/** Tell the operator something they can only acknowledge, such as a failure or an unavailable
 * feature. */
export function showNotice(opts: Prompt): void {
  if (Platform.OS === "web") {
    enqueue({ title: opts.title, message: opts.message, settle: () => {} });
    return;
  }
  Alert.alert(opts.title, opts.message);
}
